import React from 'react';
import styled from 'styled-components';
import { TrendingUp, Download, PenTool, BarChart3, Activity, Users, Award, Medal, Trophy } from 'lucide-react';

interface MonthlyPerformance {
  newLeadsReceived: number;
  signatures: number;
  conversionRate: number;
  teamRanking: number;
  totalTeamMembers: number;
}

interface LeadsPipelineSummary {
  redStatusCount: number;
  orangeStatusCount: number;
  greenStatusCount: number;
  totalActiveLeads: number;
}

interface TopSalesRep {
  userId: number;
  name: string;
  leadsCount: number;
  signaturesCount: number;
  conversionRate: number;
  ranking: number;
}

interface Props {
  performance: MonthlyPerformance;
  pipeline: LeadsPipelineSummary;
  newLeadsCount: number;
  signedThisMonthCount: number;
  topSalesReps: TopSalesRep[];
}

const Container = styled.div`
  background: white;
  border-radius: 16px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
  padding: 28px;
  margin-bottom: 28px;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 24px;
`;

const SectionIcon = styled.div`
  display: flex;
  align-items: center;
  color: var(--primary-color);
  filter: saturate(0.7) brightness(0.9);
`;

const SectionTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
`;

const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
  margin-bottom: 24px;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 767px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div<{ $accentColor: string }>`
  background: white;
  border-radius: 12px;
  padding: 24px;
  padding-top: 28px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  border: 1px solid var(--border-color);
  position: relative;
  overflow: hidden;
  transition: all 0.2s ease;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: ${props => props.$accentColor};
    filter: saturate(0.7) brightness(0.9);
  }

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
`;

const CardIcon = styled.div<{ $color: string; $noBackground?: boolean }>`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: ${props => props.$noBackground ? 'transparent' : `${props.$color}15`};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.$color};
  filter: saturate(0.7) brightness(0.9);
`;

const CardTitle = styled.div`
  font-size: 14px;
  color: var(--text-secondary);
  font-weight: 500;
`;

const CardValue = styled.div`
  font-size: 28px;
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: 4px;

  @media (max-width: 767px) {
    font-size: 22px;
  }
`;

const CardSubValue = styled.div`
  font-size: 13px;
  color: var(--text-secondary);
`;

const PipelineIndicators = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 12px;
`;

const PipelineIndicator = styled.div<{ $color: string }>`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 600;
  color: ${props => props.$color};
  filter: saturate(0.7) brightness(0.85);
`;

const PipelineDot = styled.div<{ $color: string }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${props => props.$color};
  filter: saturate(0.7) brightness(0.9);
`;

const TableContainer = styled.div`
  background: var(--background-color);
  border-radius: 10px;
  padding: 20px;
  overflow-x: auto;
`;

const TableHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;
`;

const TableIcon = styled.div`
  color: var(--primary-color);
  display: flex;
  align-items: center;
  filter: saturate(0.7) brightness(0.9);
`;

const TableTitle = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Th = styled.th`
  background: white;
  padding: 14px 16px;
  text-align: center;
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
  border-bottom: 1px solid var(--border-color);
  white-space: nowrap;
`;

const Td = styled.td`
  padding: 14px 16px;
  text-align: center;
  font-size: 14px;
  color: var(--text-primary);
  border-bottom: 1px solid var(--border-color);
  background: white;
`;

const Tr = styled.tr`
  transition: background-color 0.15s ease;

  &:hover td {
    background: var(--background-color);
  }

  &:last-child td {
    border-bottom: none;
  }
`;

const RankingBadge = styled.div<{ $rank: number }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  font-weight: 700;
  font-size: 14px;
  color: ${props => {
    if (props.$rank === 1) return '#d97706';
    if (props.$rank === 2) return '#6b7280';
    if (props.$rank === 3) return '#92400e';
    return 'var(--text-secondary)';
  }};
  filter: saturate(0.7) brightness(0.9);
`;

const PercentageBar = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const BarBackground = styled.div`
  flex: 1;
  height: 6px;
  background: var(--border-color);
  border-radius: 3px;
  overflow: hidden;
`;

const BarFill = styled.div<{ $percentage: number }>`
  height: 100%;
  width: ${props => Math.min(props.$percentage, 100)}%;
  background: ${props => {
    if (props.$percentage >= 40) return '#16a34a';
    if (props.$percentage >= 20) return '#d97706';
    return '#dc2626';
  }};
  transition: width 0.3s ease;
  border-radius: 3px;
  filter: saturate(0.7) brightness(0.9);
`;

const PercentageText = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: var(--text-primary);
  min-width: 50px;
  text-align: left;
`;

const AdminSalesMetrics: React.FC<Props> = ({
  performance,
  pipeline,
  newLeadsCount,
  signedThisMonthCount,
  topSalesReps,
}) => {
  const getRankingIcon = (rank: number) => {
    if (rank === 1) return <Trophy size={14} />;
    if (rank === 2) return <Medal size={14} />;
    if (rank === 3) return <Award size={14} />;
    return rank;
  };

  return (
    <Container>
      <SectionHeader>
        <SectionIcon>
          <TrendingUp size={20} />
        </SectionIcon>
        <SectionTitle>ביצועי מכירות החודש</SectionTitle>
      </SectionHeader>

      <CardsGrid>
        <Card $accentColor="var(--primary-color)">
          <CardHeader>
            <CardIcon $color="var(--primary-color)">
              <Download size={20} />
            </CardIcon>
            <CardTitle>לידים חדשים</CardTitle>
          </CardHeader>
          <CardValue>{performance.newLeadsReceived}</CardValue>
          <CardSubValue>התקבלו החודש</CardSubValue>
        </Card>

        <Card $accentColor="var(--success-color)">
          <CardHeader>
            <CardIcon $color="var(--success-color)">
              <PenTool size={20} />
            </CardIcon>
            <CardTitle>חתימות</CardTitle>
          </CardHeader>
          <CardValue>{performance.signatures}</CardValue>
          <CardSubValue>נחתמו החודש</CardSubValue>
        </Card>

        <Card $accentColor="#6366f1">
          <CardHeader>
            <CardIcon $color="#6366f1" $noBackground>
              <BarChart3 size={20} />
            </CardIcon>
            <CardTitle>שיעור המרה</CardTitle>
          </CardHeader>
          <CardValue>{performance.conversionRate.toFixed(1)}%</CardValue>
          <CardSubValue>
            {performance.signatures} מתוך {performance.newLeadsReceived} לידים
          </CardSubValue>
        </Card>

        <Card $accentColor="var(--warning-color)">
          <CardHeader>
            <CardIcon $color="var(--warning-color)">
              <Activity size={20} />
            </CardIcon>
            <CardTitle>Pipeline</CardTitle>
          </CardHeader>
          <CardValue>{pipeline.totalActiveLeads}</CardValue>
          <CardSubValue>לידים פעילים</CardSubValue>
          <PipelineIndicators>
            <PipelineIndicator $color="#dc2626">
              <PipelineDot $color="#dc2626" />
              {pipeline.redStatusCount}
            </PipelineIndicator>
            <PipelineIndicator $color="#d97706">
              <PipelineDot $color="#d97706" />
              {pipeline.orangeStatusCount}
            </PipelineIndicator>
            <PipelineIndicator $color="#16a34a">
              <PipelineDot $color="#16a34a" />
              {pipeline.greenStatusCount}
            </PipelineIndicator>
          </PipelineIndicators>
        </Card>
      </CardsGrid>

      {topSalesReps && topSalesReps.length > 0 && (
        <TableContainer>
          <TableHeader>
            <TableIcon>
              <Users size={18} />
            </TableIcon>
            <TableTitle>נציגי מכירות מצטיינים - TOP 5</TableTitle>
          </TableHeader>
          <Table>
            <thead>
              <tr>
                <Th>דירוג</Th>
                <Th>שם</Th>
                <Th>לידים</Th>
                <Th>חתימות</Th>
                <Th>שיעור המרה</Th>
              </tr>
            </thead>
            <tbody>
              {topSalesReps.map((rep) => (
                <Tr key={rep.userId}>
                  <Td>
                    <RankingBadge $rank={rep.ranking}>
                      {getRankingIcon(rep.ranking)}
                    </RankingBadge>
                  </Td>
                  <Td style={{ fontWeight: 600 }}>
                    {rep.name}
                  </Td>
                  <Td>{rep.leadsCount}</Td>
                  <Td style={{ fontWeight: 600, color: '#15803d' }}>
                    {rep.signaturesCount}
                  </Td>
                  <Td>
                    <PercentageBar>
                      <BarBackground>
                        <BarFill $percentage={rep.conversionRate} />
                      </BarBackground>
                      <PercentageText>{rep.conversionRate.toFixed(1)}%</PercentageText>
                    </PercentageBar>
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        </TableContainer>
      )}
    </Container>
  );
};

export default AdminSalesMetrics;
